import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { Button } from "./ui/button";
import apiClient from "../common/apiClient";
import {
  useUserProfile,
  getCityOptions,
  withSelectedCity,
} from "../contexts/UserProfileContext";

// Kept in sync with the requirement form in PropertyListed.jsx - both post to
// /brokerrequirements/add, so the option lists have to match.
const CATEGORIES = [
  "Residential Rent",
  "Residential Sell",
  "Commercial Rent",
  "Commercial Sell",
];

const RESIDENTIAL_SUBTYPES = [
  "Apartment",
  "Bungalow",
  "Tenement",
  "Penthouse",
  "Weekend Home",
  "PG",
  "Rowhouse",
  "Residential Plot",
  "Pre Leased Spaces",
];

const COMMERCIAL_SUBTYPES = [
  "Basement",
  "Commercial Plot",
  "Shed",
  "Co Working Space",
  "Factory",
  "Shop",
  "Commercial Building",
  "Godown",
  "Showroom",
  "Restaurant",
  "Industrial Land",
  "Commercial Flat",
  "Office",
  "Ware House",
  "Pre Leased Spaces",
  "Commericial Bunglow",
  "Land",
  "Agriculture Land",
  "PG House",
  "Hotel",
  "Gym",
  "Spa",
  "Hospital",
  "Salon",
  "Petrol Pump",
  "Cafe",
  "PreSchool",
  "Industrial Plot",
  "Industrial Shed",
];

const BHK_OPTIONS = [
  // "Studio Apartment",
  "1 BHK",
  "2 BHK",
  "3 BHK",
  "4 BHK",
  "5 BHK",
  "6 BHK",
  "6+ BHK",
];

const FURNISHING_OPTIONS = [
  // "Fully Furnished",
  "Furnished",
  "Semi-Furnished",
  "Kitchen-Fix",
  "Unfurnished",
];

const MAX_AREAS = 5;

const emptyForm = {
  title: "",
  customerName: "",
  type: "",
  subType: "",
  minPrice: "",
  maxPrice: "",
  minSqFt: "",
  maxSqFt: "",
  city: "",
  areas: [],
  bhk: [],
  furnishingPreferences: [],
};

// Existing requirement -> form shape. Numeric fields fall back to "" rather
// than 0 so an unset max stays an empty input instead of a literal zero.
const toForm = (requirement) => {
  if (!requirement) return emptyForm;

  const priceRange = requirement.priceRange || {};
  const sqFt = requirement.sizeRange?.sqFt || {};
  const asInput = (value) =>
    value === null || value === undefined || value === "" ? "" : String(value);

  return {
    title: requirement.title || "",
    customerName: requirement.customerName || "",
    type: requirement.type || "",
    subType: requirement.subType || "",
    minPrice: asInput(priceRange.min),
    maxPrice: asInput(priceRange.max),
    minSqFt: asInput(sqFt.min),
    maxSqFt: asInput(sqFt.max),
    city: requirement.city || "",
    areas: Array.isArray(requirement.areas) ? requirement.areas : [],
    bhk: Array.isArray(requirement.bhk) ? requirement.bhk : [],
    furnishingPreferences: Array.isArray(requirement.furnishingPreferences)
      ? requirement.furnishingPreferences
      : [],
  };
};

const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

const chipClass = (selected) =>
  `px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
    selected
      ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
  }`;

const inputClass =
  "w-full border border-gray-300 rounded-xl p-3 outline-none focus:ring-2 focus:ring-primary/20 transition-all";

// `requirement` switches the modal into edit mode - same form, but prefilled
// and PUT to the requirement instead of POSTed to /add.
const AddRequirementModal = ({ open, onClose, onCreated, requirement = null }) => {
  const isEdit = Boolean(requirement?._id);

  const [form, setForm] = useState(emptyForm);
  const [areaInput, setAreaInput] = useState("");
  const [areaSuggestions, setAreaSuggestions] = useState([]);
  const [areaFocused, setAreaFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isCommercial = form.type.includes("Commercial");
  const subTypes = isCommercial ? COMMERCIAL_SUBTYPES : RESIDENTIAL_SUBTYPES;
  // Cities the logged-in user has access to (PropertyAccessCity). The live
  // profile wins once it loads; the cookie covers the render before that.
  const { propertyAccessCities } = useUserProfile();
  const cityOptions = withSelectedCity(
    propertyAccessCities?.length ? propertyAccessCities : getCityOptions(),
    form.city
  );

  // Reset every time the modal is reopened so a cancelled draft never leaks
  // into the next requirement - or, when editing, so the form always reflects
  // the requirement that was just clicked.
  useEffect(() => {
    if (!open) return;
    setForm(toForm(requirement));
    setAreaInput("");
    setAreaSuggestions([]);
    setError("");
  }, [open, requirement]);

  // Same suggest endpoint the PropertyListed requirement form uses.
  useEffect(() => {
    if (!open || !areaInput.trim()) {
      setAreaSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      const response = await apiClient(
        "GET",
        `/cjidnvij/ceksfbuebijn/user/areas/suggest?query=${encodeURIComponent(
          areaInput.trim()
        )}&moduleType=requirement&caseInsensitive=true`,
        null,
        { headers: { "Content-Type": "application/json" } }
      );

      if (!response?.success || !response.data) {
        setAreaSuggestions([]);
        return;
      }

      const list = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      setAreaSuggestions(
        list.map((item) => ({
          name: item.name || item.areaName || item,
          _id: item._id || item.id || item.name,
        }))
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [areaInput, open]);

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleInList = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));

  const addArea = (name) => {
    const value = (name || "").trim();
    if (!value) return;

    setForm((prev) => {
      const exists = prev.areas.some(
        (area) => area.toLowerCase() === value.toLowerCase()
      );
      if (exists || prev.areas.length >= MAX_AREAS) return prev;
      return { ...prev, areas: [...prev.areas, value] };
    });

    setAreaInput("");
    setAreaSuggestions([]);
  };

  const removeArea = (index) =>
    setForm((prev) => ({
      ...prev,
      areas: prev.areas.filter((_, i) => i !== index),
    }));

  const validate = () => {
    if (!form.type) return "Please select a property category.";
    if (!form.subType) return "Please select a property type.";
    if (form.minPrice === "") return "Minimum price is required.";
    if (form.maxPrice !== "" && Number(form.maxPrice) < Number(form.minPrice)) {
      return "Maximum price cannot be less than minimum price.";
    }
    if (
      form.minSqFt !== "" &&
      form.maxSqFt !== "" &&
      Number(form.maxSqFt) < Number(form.minSqFt)
    ) {
      return "Maximum sq.ft cannot be less than minimum sq.ft.";
    }
    if (!form.city) return "Please select a city.";
    if (form.areas.length === 0) return "Please add at least one area.";
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const token = Cookies.get("token");
    if (!token) {
      setError("Unauthorized: Please login first.");
      return;
    }

    setError("");
    setSubmitting(true);

    // Shape matches the requirementPayload built in PropertyListed.jsx.
    // brokerName/contactNumber come from the logged in user rather than being
    // re-typed - the backend requires both.
    const payload = {
      type: form.type,
      title: form.title,
      customerName: form.customerName,
      subType: form.subType,
      // On edit, keep whatever the requirement was created with - the cookies
      // are only a fallback so the backend's required fields are never blank.
      brokerName: requirement?.brokerName || Cookies.get("name") || "",
      contactNumber: requirement?.contactNumber || Cookies.get("number") || "",
      priceRange: {
        min: parseInt(form.minPrice, 10),
        max: form.maxPrice !== "" ? parseInt(form.maxPrice, 10) : null,
      },
      areas: form.areas.filter(Boolean),
      bhk: form.bhk,
      sizeRange: {
        sqFt: {
          min: form.minSqFt !== "" ? parseInt(form.minSqFt, 10) : 0,
          max: form.maxSqFt !== "" ? parseInt(form.maxSqFt, 10) : null,
        },
        sqYd: { min: 0, max: null },
      },
      furnishingPreferences: form.furnishingPreferences,
      city: form.city,
      // Editing this form never touches the description or the original listing
      // date, so carry both through instead of blanking them.
      description: requirement?.description || "",
      ...(isEdit && requirement.listedDate
        ? { listedDate: requirement.listedDate }
        : {}),
    };

    try {
      const response = isEdit
        ? await apiClient(
            "PUT",
            `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${requirement._id}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          )
        : await apiClient(
            "POST",
            "/cjidnvij/ceksfbuebijn/user/brokerrequirements/add",
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

      const body = response?.data || {};
      const ok = response?.status === 200 || body?.success === true;
      const failMessage = isEdit
        ? "Failed to update requirement."
        : "Failed to create requirement.";

      if (!ok) {
        setError(body.error || body.message || failMessage);
        return;
      }

      toast.success(
        body.message ||
          (isEdit
            ? "Requirement updated successfully!"
            : "Requirement created successfully!")
      );
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.message ||
          (isEdit
            ? "Failed to update requirement."
            : "Failed to create requirement.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? "Edit Requirement" : "Add Requirement"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Premise name + customer name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Premise Name
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", capitalizeWords(e.target.value))}
                placeholder="Enter premise name"
                className={inputClass}
              />
            </div> */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Customer Name
              </label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) =>
                  update("customerName", capitalizeWords(e.target.value))
                }
                placeholder="Enter customer name"
                className={inputClass}
              />
            </div>
          </div>

          {/* Category + subtype */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Property Category *
              </label>
              <select
                value={form.type}
                onChange={(e) => {
                  const nextType = e.target.value;
                  // Subtype and BHK lists depend on the category, so clear them.
                  setForm((prev) => ({
                    ...prev,
                    type: nextType,
                    subType: "",
                    bhk: nextType.includes("Commercial") ? [] : prev.bhk,
                  }));
                }}
                className={`${inputClass} bg-white`}
              >
                <option value="">Select Category</option>
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Property Type *
              </label>
              <select
                value={form.subType}
                onChange={(e) => update("subType", e.target.value)}
                disabled={!form.type}
                className={`${inputClass} bg-white disabled:bg-gray-100 disabled:cursor-not-allowed`}
              >
                <option value="">
                  {form.type ? "Select Property Type" : "Select category first"}
                </option>
                {subTypes.map((subType) => (
                  <option key={subType} value={subType}>
                    {subType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Price range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Min Price *
              </label>
              <input
                type="number"
                value={form.minPrice}
                onChange={(e) => update("minPrice", e.target.value)}
                placeholder="Minimum price"
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Max Price
              </label>
              <input
                type="number"
                value={form.maxPrice}
                onChange={(e) => update("maxPrice", e.target.value)}
                placeholder="Maximum price - optional"
                className={inputClass}
              />
            </div>
          </div>

          {/* Size range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Min Sq.Ft
              </label>
              <input
                type="number"
                value={form.minSqFt}
                onChange={(e) => update("minSqFt", e.target.value)}
                placeholder="Minimum sq.ft"
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">
                Max Sq.Ft
              </label>
              <input
                type="number"
                value={form.maxSqFt}
                onChange={(e) => update("maxSqFt", e.target.value)}
                placeholder="Maximum sq.ft - optional"
                className={inputClass}
              />
            </div>
          </div>

          {/* City + areas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">City *</label>
              <select
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                className={`${inputClass} bg-white`}
              >
                <option value="">Select City</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 relative">
              <label className="text-sm font-semibold text-gray-700">
                Area * (max {MAX_AREAS})
              </label>
              <input
                type="text"
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                onFocus={() => setAreaFocused(true)}
                onBlur={() => setTimeout(() => setAreaFocused(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addArea(areaInput);
                  }
                }}
                disabled={form.areas.length >= MAX_AREAS}
                placeholder={
                  form.areas.length >= MAX_AREAS
                    ? "Area limit reached"
                    : "Search for area..."
                }
                className={`${inputClass} disabled:bg-gray-100 disabled:cursor-not-allowed`}
              />
              {areaFocused && areaSuggestions.length > 0 && (
                <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-auto">
                  {areaSuggestions.map((item) => (
                    <button
                      key={item._id || item.name}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addArea(item.name);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {form.areas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.areas.map((area, index) => (
                <div
                  key={`${area}-${index}`}
                  className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full hover:bg-gray-200"
                >
                  <span className="text-gray-700 text-sm">{area}</span>
                  <button
                    type="button"
                    onClick={() => removeArea(index)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* BHK - residential only, same rule as the PropertyListed form */}
          {!isCommercial && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">
                BHK Configuration
              </label>
              <div className="flex flex-wrap gap-2">
                {BHK_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleInList("bhk", option)}
                    className={chipClass(form.bhk.includes(option))}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Furnishing preferences */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">
              Furnishing Preferences
            </label>
            <div className="flex flex-wrap gap-2">
              {FURNISHING_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleInList("furnishingPreferences", option)}
                  className={chipClass(
                    form.furnishingPreferences.includes(option)
                  )}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 text-sm text-center font-medium">
              {error}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 flex gap-3 bg-gray-50/50">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 h-12 rounded-xl gradient-primary"
          >
            {submitting
              ? "Saving..."
              : isEdit
              ? "Update Requirement"
              : "Save Requirement"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddRequirementModal;
